import React, { useCallback, useState, useMemo } from 'react'

import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Typography,
  Chip,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import DeleteIcon from '@material-ui/icons/Delete'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import { makeStyles } from '@material-ui/core/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faCopy, faLock, faLockOpen, faEdit } from '@fortawesome/pro-solid-svg-icons'

import { format } from 'date-fns'

import { ConfirmationDialog } from '@scoir/modals'

import { useDeleteRound } from '../AppRoundProvider'
import { AppRound } from '../types'

import AppRoundModalTrigger from './AppRoundModal'

const useStyles = makeStyles((theme) => ({
  checkIcon: {
    color: theme.palette.success.main,
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  expandBtn: {
    marginLeft: 'auto',
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  cardTypography: {
    fontSize: theme.typography.pxToRem(16),
  },
  cardContentContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gridGap: theme.spacing(1),
  },
  dateLabel: {
    marginRight: theme.spacing(1),
  },
  chipContainer: {
    fontSize: theme.typography.pxToRem(14),
  },
}))

export const AppRoundCard = ({ round, canConfigure }: { round: AppRound; canConfigure: boolean }) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const [hasDeleteError, setHasDeleteError] = useState(false)
  const classes = useStyles()

  const deleteRound = useDeleteRound()
  const confirmDelete = useCallback(() => {
    setHasDeleteError(false)
    deleteRound(round.id).catch(() => {
      setHasDeleteError(true)
    })
  }, [deleteRound, round.id])
  const [expanded, setExpanded] = React.useState(false)
  const handleExpandClick = () => {
    setExpanded(!expanded)
  }
  const { deadlineDate, materialsDeadlineDate, cutoffDate, updatedAt } = useMemo(
    () => ({
      deadlineDate: round.deadlineDate ? format(new Date(round.deadlineDate), 'M/d') : '',
      materialsDeadlineDate: round.materialsDeadlineDate ? format(new Date(round.materialsDeadlineDate), 'M/d') : '',
      cutoffDate: round.cutoffDate ? format(new Date(round.cutoffDate), 'M/d') : '',
      updatedAt: round.updatedAt ? format(new Date(round.updatedAt), "M/d/yyyy h:mm aaaaa'm'") : '',
    }),
    [round.cutoffDate, round.materialsDeadlineDate, round.deadlineDate, round.updatedAt],
  )
  return (
    <>
      <Card>
        <CardHeader
          title={round.name}
          titleTypographyProps={{
            className: classes.cardTypography,
            variant: 'body1',
            color: 'textPrimary',
            component: 'h3',
          }}
        />
        <Divider />
        <CardContent>
          {round.updatedAt ? (
            <Box
              display="flex"
              alignItems="center"
            >
              <Box mr={1}>
                <CheckCircleIcon className={classes.checkIcon} />
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                component="p"
              >
                Last updated by {round.updatedByName} on {updatedAt}
              </Typography>
            </Box>
          ) : (
            <Box
              display="flex"
              alignItems="center"
            >
              <Box mr={1}>
                <ErrorIcon className={classes.warningIcon} />
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                component="p"
              >
                This round has not been updated recently
              </Typography>
            </Box>
          )}
          <Collapse
            in={expanded}
            timeout="auto"
          >
            <Box pt={1}>
              <Box className={classes.cardContentContainer}>
                {round.cutoffDate ? (
                  <>
                    <Typography component="span">
                      Deadline&nbsp;
                      <strong>{deadlineDate}</strong>
                    </Typography>
                    <Typography component="span">
                      {/* Figma says Cut-Off?  */}
                      Cutoff&nbsp;
                      <strong>{cutoffDate}</strong>
                    </Typography>
                    <Typography component="span">
                      Materials&nbsp;
                      <strong>{materialsDeadlineDate}</strong>
                    </Typography>
                  </>
                ) : (
                  <Typography component="span">No deadlines</Typography>
                )}
              </Box>
              <Box
                mt={1}
                className={classes.cardContentContainer}
              >
                <Chip
                  size="small"
                  label={
                    <>
                      <span className={classes.chipContainer}>
                        <FontAwesomeIcon icon={(round.binding ? faLock : faLockOpen) as IconProp} />
                      </span>
                      &nbsp;
                      {round.binding ? 'Binding' : 'Non-Binding'}
                    </>
                  }
                />
                <Chip
                  size="small"
                  color={round.applicantType === 'firstYear' ? 'primary' : 'secondary'}
                  label={round.applicantType === 'firstYear' ? 'First-Year Applicants' : 'Transfer Applicants'}
                />
              </Box>
            </Box>
          </Collapse>
        </CardContent>
        <Divider />
        <CardActions disableSpacing>
          <AppRoundModalTrigger
            round={round}
            disabled={!canConfigure}
            Component={IconButton}
            aria-label={`Edit Round ${round.name}`}
            color="primary"
            variant="contained"
          >
            <FontAwesomeIcon icon={faEdit as IconProp} />
          </AppRoundModalTrigger>
          <AppRoundModalTrigger
            copy
            round={round}
            Component={IconButton}
            disabled={!canConfigure}
            aria-label={`Copy Deadline ${round.name}`}
            color="primary"
            variant="contained"
          >
            <FontAwesomeIcon icon={faCopy as IconProp} />
          </AppRoundModalTrigger>
          <IconButton
            disabled={!canConfigure}
            aria-label={`Delete Round ${round.name}`}
            color="primary"
            onClick={() => {
              setIsConfirmingDelete(true)
            }}
          >
            <DeleteIcon />
          </IconButton>
          <IconButton
            className={`${classes.expandBtn} ${expanded ? classes.expandOpen : classes.expand}`}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Hide' : 'Show'} ${round.name} content`}
          >
            <ExpandMoreIcon />
          </IconButton>
        </CardActions>
      </Card>
      <ConfirmationDialog
        open={!!isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        onConfirm={confirmDelete}
        headerText="Confirm Delete Deadline"
      >
        <Typography>
          Are your sure you want to delete {round.applicantType === 'firstYear' ? 'First-Year' : 'Transfer'}{' '}
          {round.name}?
        </Typography>
        {hasDeleteError && (
          <Box
            my={2}
            textAlign="center"
          >
            <Typography
              role="alert"
              color="error"
            >
              An error occurred while deleting your app round, please try again another time.
            </Typography>
          </Box>
        )}
      </ConfirmationDialog>
    </>
  )
}
export const AppRoundsGrid = ({ rounds, canConfigure }: { rounds: Array<AppRound>; canConfigure: boolean }) => (
  <Grid
    container
    spacing={2}
  >
    {rounds.map((round) => (
      <Grid
        item
        key={round.id}
        xs={12}
        sm={6}
        lg={4}
      >
        <AppRoundCard
          round={round}
          canConfigure={canConfigure}
        />
      </Grid>
    ))}
  </Grid>
)
